import React, { FunctionComponent, useMemo } from 'react'
import { Navigate, useNavigate } from 'react-router'

import { getCurrentQuote, getInsuredProfile, Profile, Proposal } from '../../../domain'
import { useFormValidation } from '../../../hooks/adjustments/formValidation'
import {
  useAdditionalProfileFormData,
  useAdditionalProfileProposalAdjustment,
} from '../../../hooks/adjustments/proposal'
import {
  TExtends,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { useRequiredParams, useUpButton } from '../../../infrastructure/routing'
import { AdditionalProfileManagementConfig } from '../../../state/configuration'
import { FormErrors } from '../../formBuilder'
import { FullPage } from '../../templates'
import { useProposal } from '../proposal/ProposalProvider'

export interface RenewalAdditionalProfilePageProps {
  config: AdditionalProfileManagementConfig
}

const tKey = TKey('pages.additional-profile')

const hasReadOnly = (profileCode: string | undefined, proposal: Proposal) =>
  profileCode &&
  proposal.quote.insuredProfiles.find((it) => it.profileCode === profileCode)

const Content: FunctionComponent<
  RenewalAdditionalProfilePageProps & { profile: Profile }
> = ({ config, profile }) => {
  const { t } = useWriskTranslation()

  const navigate = useNavigate()
  const upButton = useUpButton({ url: '../../..', id: 'renewal' })
  const { proposal } = useProposal()

  const data = useAdditionalProfileFormData(config.form, profile)
  const options = useMemo(
    () =>
      hasReadOnly(profile.profileCode, proposal) ? undefined : { disableReadOnly: true },
    [profile, proposal],
  )
  const onSubmitAdjustment = useAdditionalProfileProposalAdjustment({
    form: config.form,
    applyRating: true,
    profile: profile,
    onComplete: () => navigate('../../..'),
  })

  const onValidateForm = useFormValidation({
    validationProfile: config.validationProfile,
    onSuccess: onSubmitAdjustment.execute,
  })

  return (
    <FullPage
      pageId='renewal-additional-profile'
      upButton={upButton}
      header={t(tKey('header'))}
    >
      <FormErrors
        id='additional-profile'
        data={data}
        config={config.form}
        options={options}
        onSubmit={onValidateForm.execute}
        loading={onValidateForm.loading}
        tKey={TExtends(tKey, 'form')}
        t={t}
      />
    </FullPage>
  )
}

export const RenewalAdditionalProfilePage: FunctionComponent<
  RenewalAdditionalProfilePageProps
> = ({ config }) => {
  const { profileCode } = useRequiredParams<{ profileCode: string }>()

  const { proposal } = useProposal()

  const quote = getCurrentQuote(proposal)
  const profile = getInsuredProfile(quote, profileCode)

  return profile ? (
    <Content profile={profile} config={config} />
  ) : (
    <Navigate to='../../..' />
  )
}

export const RenewalAdditionalProfileCreatePage: FunctionComponent<
  RenewalAdditionalProfilePageProps
> = ({ config }) => <Content profile={{ profileCode: 'p1', data: {} }} config={config} />
